// 표준화된 GAUGE 차트 옵션 생성
import { formatValue } from '../../../utils/numberFormatter';
import { applyValueStyle, applyUnitStyle, applyTitleStyle } from './applyCommonStyles';

export const generateGaugeChartOption = (data, baseOption, options = {}) => {
  // 표준 구조: { series: [{ name: "게이지명", values: [숫자값] }] }
  const series = data.series || [];
  
  // 빈 데이터 처리 - 기본값으로 렌더링
  if (series.length === 0) {
    console.warn('[Gauge Chart] No series data provided, using default values');
    // 기본값으로 게이지 렌더링
    data.series = [{ name: '', values: [0] }];
  }
  
  const firstSeries = data.series[0];
  const gaugeValue = Array.isArray(firstSeries.values) ? firstSeries.values[0] : firstSeries.values;
  const gaugeName = firstSeries.name || '';
  
  // 옵션에서 추가 설정 추출 (기본 options 우선, 없으면 advanced에서)
  const unit = options.unit || options.advanced?.unit || '';
  const min = options.min !== undefined ? options.min : (options.advanced?.min !== undefined ? options.advanced.min : 0);
  const max = options.max !== undefined ? options.max : (options.advanced?.max !== undefined ? options.advanced.max : 100);
  
  // displayMode === 'value' 처리 (숫자만 표시)
  if (options.displayMode === 'value' || options.advanced?.displayMode === 'value') {
    const valueOnlyOption = {
      ...baseOption,
      tooltip: { show: false },
      series: [{
        type: 'gauge',
        radius: '0%', // 게이지 시각적 요소 숨김
        center: ['50%', '50%'],
        min: min,
        max: max,
        data: [{ value: gaugeValue, name: gaugeName }],
        detail: {
          formatter: function(value) {
            const formattedValue = formatValue(value);
            return `${formattedValue}${unit}`;
          },
          fontSize: options.advanced?.valueFontSize || options.advanced?.detailFontSize || 40,
          fontWeight: options.advanced?.valueFontWeight || 'bold',
          color: options.advanced?.valueColor || '#3574D9',
          offsetCenter: [0, '0%']
        },
        title: {
          show: !!gaugeName,
          offsetCenter: [0, '-40%'],
          fontSize: 14,
          color: '#666',
          fontWeight: 'normal'
        },
        // 게이지 시각적 요소들 비활성화
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false },
        pointer: { show: false },
        progress: { show: false },
        anchor: { show: false }
      }]
    };
    
    // 값만 표시할 때는 그리드, 축, 범례 등 불필요
    delete valueOnlyOption.grid;
    delete valueOnlyOption.xAxis;
    delete valueOnlyOption.yAxis;
    delete valueOnlyOption.legend;
    
    return valueOnlyOption;
  }

  // 일반 게이지 차트 로직
  const splitNumber = options.advanced?.splitNumber || 10;
  const detailFontSize = options.advanced?.detailFontSize || 32;
  const titleFontSize = options.advanced?.titleFontSize || 16;

  const option = {
    ...baseOption,
    tooltip: {
      formatter: (params) => {
        let tooltipText = '';
        if (params.name) {
          tooltipText += `${params.name}<br/>`;
        }
        tooltipText += `${params.marker}${params.seriesName}: <b>${params.value}${unit}</b>`;
        return tooltipText;
      },
      confine: true,
    },
    series: [
      {
        name: baseOption.title?.text || 'Gauge',
        type: 'gauge',
        min: min,
        max: max,
        splitNumber: splitNumber,
        radius: options.advanced?.radius || '75%',
        center: ['50%', '60%'],
        axisLine: {
          lineStyle: {
            width: options.advanced?.axisLineWidth || 20,
            color: options.advanced?.axisLineColor || options.colorSteps || [
              [0.2, '#67e0e3'],  // 0-20%: 청록색
              [0.4, '#37da7e'],  // 20-40%: 녹색  
              [0.6, '#ffdb5c'],  // 40-60%: 노란색
              [0.8, '#ff9f7f'],  // 60-80%: 주황색
              [1, '#fb7293']     // 80-100%: 빨간색
            ]
          }
        },
        axisTick: {
          show: options.advanced?.showAxisTick !== undefined ? options.advanced.showAxisTick : true,
          length: options.advanced?.axisTickLength || 8,
          lineStyle: {
            color: 'auto',
            width: 1
          }
        },
        splitLine: {
          show: options.advanced?.showSplitLine !== undefined ? options.advanced.showSplitLine : true,
          length: options.advanced?.splitLineLength || 15,
          lineStyle: {
            color: 'auto',
            width: 2
          }
        },
        axisLabel: {
          show: options.advanced?.showAxisLabel !== undefined ? options.advanced.showAxisLabel : true,
          color: 'auto',
          fontSize: 12,
          distance: 20,
          formatter: function (value) {
            return formatValue(value);
          }
        },
        pointer: {
          show: options.advanced?.showPointer !== undefined ? options.advanced.showPointer : true,
          length: '70%',
          width: 8,
          offsetCenter: [0, '-5%'],
          itemStyle: {
            color: 'auto'  // 포인터 색상도 자동으로 설정
          }
        },
        anchor: {
          show: options.advanced?.showAnchor !== undefined ? options.advanced.showAnchor : true,
          showAbove: true,
          size: 6,
          itemStyle: {
            borderWidth: 2,
            borderColor: '#fff'
          }
        },
        progress: {
          show: options.advanced?.showProgress !== undefined ? options.advanced.showProgress : true,
          overlap: false,
          roundCap: true,
          clip: false,
          itemStyle: {
            borderWidth: 0,
            borderColor: '#464646'
          }
        },
        detail: {
          show: options.advanced?.showDetail !== undefined ? options.advanced.showDetail : true,
          valueAnimation: true,
          fontSize: detailFontSize,
          fontWeight: 'bold',
          color: 'auto',
          offsetCenter: [0, '35%'],
          formatter: function (value) {
            const formattedValue = formatValue(value);
            return `${formattedValue}${unit}`;
          }
        },
        title: {
          show: !!gaugeName,
          fontSize: titleFontSize,
          color: '#666',
          fontWeight: 'normal',
          offsetCenter: [0, '55%']
        },
        data: [
          {
            value: gaugeValue,
            name: gaugeName
          }
        ]
      }
    ]
  };

  delete option.legend;
  delete option.grid;
  delete option.xAxis;
  delete option.yAxis;

  // showTitle이 false인 경우 제목 제거
  if (options.showTitle === false) {
    delete option.title;
  } else if (option.title) {
    option.title.left = 'center';
    option.title.top = '5%';
    // 제목 스타일 개선
    option.title.textStyle = {
      fontSize: 20,
      fontWeight: '600',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      color: '#2c3e50',
      lineHeight: 1.2,
      padding: [0, 0, 8, 0]
    };
  }

  // 공통 스타일 적용
  if (options.style) {
    // 게이지 차트는 title, value, unit 스타일만 적용
    if (option.title && options.style.fonts?.title) {
      option.title = applyTitleStyle(option.title, options.style);
    }
    
    // 게이지의 detail(value)과 axisLabel(unit) 스타일 적용
    if (option.series && option.series[0]) {
      if (options.style.fonts?.value && option.series[0].detail) {
        option.series[0].detail = {
          ...option.series[0].detail,
          ...applyValueStyle(option.series[0].detail, options.style),
          formatter: option.series[0].detail.formatter // formatter 유지
        };
      }
      
      if (options.style.fonts?.unit && option.series[0].axisLabel) {
        option.series[0].axisLabel = {
          ...option.series[0].axisLabel,
          ...applyUnitStyle(option.series[0].axisLabel, options.style),
          formatter: option.series[0].axisLabel.formatter // formatter 유지
        };
      }
    }
  }

  // transparent 처리 (ECharts 차트 배경 투명도)
  // showFrame은 DashboardComponentRenderer에서 처리하므로 여기서는 transparent만 처리
  if ((options.transparent === true || options.transparent === 'true')) {
    // ECharts 전체 배경을 투명하게 설정
    option.backgroundColor = 'transparent';
  }

  return option;
}; 